import React, { useState, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import PropTypes from 'prop-types';
import { validatePropTypeDate, validatePropTypeInterval } from '../../../common/utils';
import withLocalization from '../../higherOrderComponents/withLocalization';
import DayPickerNavBar from './_DayPickerNavBar';

const today = new Date();
const currentYear = today.getFullYear();
const currentMonth = today.getMonth();

const DayPickerController = props => {
  const {
    year: passedYear,
    month: passedMonth,
    day: passedDay
  } = props;

  const {
    language,
    translate,
    modifiers,
    defaultDate = new Date(passedYear, passedMonth, passedDay),
    defaultDateMin = new Date(currentYear - 120, currentMonth),
    defaultDateMax = new Date(currentYear + 14, 11),
    onDayClick,
    updateDateInputState
  } = props;

  const [year, setYear] = useState(defaultDate.getFullYear());
  const [month, setMonth] = useState(defaultDate.getMonth());
  const [day, setDay] = useState(defaultDate.getDay());

  const getTranslatedLabel = translationId => translate(
    `customDayPicker/controller-button-month-${translationId}-title`
  );

  const getTranslatedDateParts = function (datePartName, numberOfDateParts) {
    return new Array(numberOfDateParts).fill().map((_, datePartIndex) => (
      translate(`dates/${datePartName}-${datePartIndex + 1}`))
    );
  };

  const handleYearMonthChange = function (inputYear, inputMonth) {
    if (updateDateInputState) {
      updateDateInputState({
        inputYear,
        inputMonth
      });
    }

    else {
      setYear(inputYear);
      setMonth(inputMonth);
    }
  };

  const translatedMonths = getTranslatedDateParts('month', 12);
  const translatedDaysLong = getTranslatedDateParts('day', 7);
  const translatedDaysShort = translatedDaysLong.map(translatedDay => translatedDay.slice(0, 2));
  const dayPickerMonth = new Date(year, month);

  useEffect(() => {
    if (passedYear !== year) {
      setYear(passedYear);
    }

    if (passedMonth !== month) {
      setMonth(passedMonth);
    }

    if (passedDay !== day) {
      setDay(passedDay);
    }
  }, [passedYear, passedMonth, passedDay]);

  return (
    <DayPicker
      locale={language}
      labels={{
        nextMonth: getTranslatedLabel('next'),
        previousMonth: getTranslatedLabel('prev')
      }}
      months={translatedMonths}
      weekdaysLong={translatedDaysLong}
      weekdaysShort={translatedDaysShort}
      month={dayPickerMonth}
      fromMonth={defaultDateMin}
      toMonth={defaultDateMax}
      onDayClick={onDayClick}
      modifiers={modifiers}
      navbarElement={(
        <DayPickerNavBar
          language={language}
          translatedMonths={translatedMonths}
          month={dayPickerMonth}
          monthMin={defaultDateMin}
          monthMax={defaultDateMax}
          handleYearMonthChange={params => handleYearMonthChange(params)}
        />
      )}
    />
  );
};

DayPickerController.propTypes = {
  onDayClick: PropTypes.func.isRequired,
  updateDateInputState: PropTypes.func.isRequired,
  year: (props, propName, componentName) => validatePropTypeInterval(props, propName, componentName, 1900, 2100, true),
  month: (props, propName, componentName) => validatePropTypeInterval(props, propName, componentName, 0, 11, true),
  day: (props, propName, componentName) => validatePropTypeInterval(props, propName, componentName, 1, 31),
  defaultDate: (props, propName, componentName) => validatePropTypeDate(props, propName, componentName),
  defaultDateMin: (props, propName, componentName) => validatePropTypeDate(props, propName, componentName),
  defaultDateMax: (props, propName, componentName) => validatePropTypeDate(props, propName, componentName),
  modifiers: PropTypes.object,
  disabledDays: PropTypes.object
};

export default withLocalization(DayPickerController, [
  'elements/customDayPicker',
  'general/dates'
]);